import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorParser } from '@app/classes';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { inLocal } from '@app/functions';
import { AutomatedEmailSettings } from '@app/models/recruiting/automated-email-settings.model';
import { JobBoard } from '@app/models/recruiting/job-board.model';
import { JobBoardService } from '@app/modules/applicant-tracker/services/job-board.service';
import { RecruitingModuleMenuService } from '@app/modules/applicant-tracker/services/menus/recruiting-module-menu.service';
import { AuthService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { FileHelperService } from '@app/services/file-helper.service';
import { environment } from '@env/environment';
import { AnalyticService, NotifyService } from 'app/services/index';
import cloneDeep from 'lodash-es/cloneDeep';

const isNewFile = (file: any) => {
    return file && !file.id;
};

@Component({
    templateUrl: './job-board-settings-show.template.html',
    styleUrls: ['./job-board-settings-show.style.scss'],
})
export class JobBoardSettingsShowView implements OnInit {
    isLoading = true;
    isEditJobBoardModalOpen = false;
    isEditBrandAssetsModalOpen = false;
    isEditAdvanceStylesModalOpen = false;
    isEmbeddedJobBoardModalOpen = false;

    hasAutomatedEmailSettings = false;
    hasResponsiveJobBoard = false;

    jobBoard: JobBoard = null;
    automatedEmailSettings: AutomatedEmailSettings;
    logoPath: string;
    jobBoardAddress: string;
    private untouchedJobBoard: JobBoard;

    constructor(
        private notify: NotifyService,
        public route: ActivatedRoute,
        private featureService: FeatureService,
        private fileHelperService: FileHelperService,
        private analyticService: AnalyticService,
        private jobBoardService: JobBoardService,
        private router: Router,
        private auth: AuthService,
        public recruitingModuleMenuService: RecruitingModuleMenuService
    ) {}

    async ngOnInit(): Promise<void> {
        try {
            this.hasAutomatedEmailSettings = await this.featureService.has(FeatureFlag.automatedEmailSettings);
            this.automatedEmailSettings = this.hasAutomatedEmailSettings
                ? await this.fetchAutomatedEmailSettings()
                : null;
            this.hasResponsiveJobBoard = await this.featureService.has(FeatureFlag.jobBoardV2MobileViews);
        } catch (err) {
            this.notify.error(ErrorParser.parse(err) || 'applicant-tracker.couldNotLoadFeatureFlags');
        }

        await this.loadSettings();
        this.setJobBoardAddress();
    }

    openEditJobBoard(): void {
        this.isEditJobBoardModalOpen = true;
    }

    openEditBrandAssets(): void {
        this.isEditBrandAssetsModalOpen = true;
    }

    openEditAdvanceStyles(): void {
        this.isEditAdvanceStylesModalOpen = true;
    }

    openEmbeddedJobBoard(): void {
        this.isEmbeddedJobBoardModalOpen = true;
    }

    editAutomatedEmailSettings(): void {
        this.router.navigate(['/recruiting', 'settings', 'automatedEmail', 'edit']);
    }

    setUpAutomatedEmailSettings(): void {
        this.router.navigate(['/recruiting', 'settings', 'automatedEmail', 'setUp']);
    }

    async onSave(form: NgForm, formName: string): Promise<void> {
        if (!form.valid) {
            return;
        }

        this.isLoading = true;
        this.isEditJobBoardModalOpen = false;
        this.isEditBrandAssetsModalOpen = false;
        this.isEditAdvanceStylesModalOpen = false;

        // Pass back a flag to indicate advanced styles should be used
        // If this is not sent back, the back end will not save the details
        // advanced_styles is NOT a database field
        if (this.jobBoard.customCssUrl || this.jobBoard.customCss) {
            this.jobBoard.advancedStyles = true;
        }

        this.jobBoard.logoId = await this.determineLogoId();
        this.jobBoard.companyId = this.auth.company.id;

        try {
            await this.jobBoard.save();

            this.notify.success('applicant-tracker.settingsSaved');
            if (formName === 'basicInformation') {
                this.analyticService.trackEvent(AnalyticEvents.RecruitingJobBoardYourJobBoardEditSave);
            }
            if (formName === 'jobBoardVisuals') {
                this.analyticService.trackEvent(AnalyticEvents.RecruitingJobBoardBrandAssetsEditSave);
            }
            if (formName === 'jobAdvancedStyles') {
                this.analyticService.trackEvent(AnalyticEvents.RecruitingJobBoardAdvancedStylesEditSave);
            }

            await this.loadSettings();
        } catch (err) {
            this.isLoading = false;
            this.onCancel();
            this.notify.error(ErrorParser.parse(err) || 'applicant-tracker.couldNotSaveSettings');
        }
    }

    onCancel(): void {
        this.isEditJobBoardModalOpen = false;
        this.isEditBrandAssetsModalOpen = false;
        this.isEditAdvanceStylesModalOpen = false;
        this.isEmbeddedJobBoardModalOpen = false;

        this.jobBoard = cloneDeep(this.untouchedJobBoard);
    }

    /**
     * Not implemented
     */
    onDelete(): void {}

    goToJobBoardAddress(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingJobBoardClickJobBoardAddressURL);
        const jobBoardUrl = this.jobBoardAddress;
        window.open(jobBoardUrl, '_blank').focus();
    }

    setJobBoardAddress(): void {
        if (inLocal()) {
            this.jobBoardAddress = `http://${this.jobBoard.companySlug.toLowerCase()}.job-board.localhost`;
            return;
        }

        this.jobBoardAddress = `https://${this.jobBoard.companySlug.toLowerCase()}.${environment.jobBoardDomain}`;
    }

    private async fetchAutomatedEmailSettings(): Promise<AutomatedEmailSettings | null | undefined> {
        const [[automatedEmailSettings]] = await AutomatedEmailSettings.all();

        return automatedEmailSettings;
    }

    private async loadSettings(): Promise<void> {
        this.isLoading = true;

        try {
            this.jobBoard = await this.jobBoardService.getBoard(this.auth.company);

            this.untouchedJobBoard = cloneDeep(this.jobBoard);

            this.logoPath = this.jobBoard.logoId ? this.fileHelperService.path(this.jobBoard.logoId) : null;
        } catch (err) {
            this.notify.error(ErrorParser.parse(err) || 'applicant-tracker.couldNotLoadJobBoard');
        }

        this.isLoading = false;
    }

    /**
     * If the logo file is null, that means the user has cleared it, and we
     * must save null as the id.
     *
     * If a new file is selected, it won't be a model, and will not have an ID
     * In which case, we must save a new file and use that ID.
     *
     * Otherwise, just return the current ID.
     */
    private async determineLogoId(): Promise<number | null> {
        if (this.jobBoard.logoFile === null) {
            return null;
        }

        if (isNewFile(this.jobBoard.logoFile)) {
            const savedFileId = (await this.fileHelperService.store(this.jobBoard.logoFile)).id;

            return savedFileId;
        }

        return this.jobBoard.logoId;
    }
}
